import { useEffect, useState, useCallback } from 'react';
import Classes from 'Api/Classes';

/**
 * Hook to load class users for a given class.
 * Uses Classes.getClassUsers API.
 *
 * @param {string} classId - The class ID to fetch users for
 * @param {Object} [opts={}] - Options passed to getClassUsers
 * @param {string} [opts.include] - e.g. 'students'
 * @param {boolean} [opts.studentLogin] - Include student login info
 * @param {string} [opts.grades] - Grade filter
 * @returns {{ data: any, users: Array, loading: boolean, error: Error|null, refetch: () => Promise<void> }}
 */
const useClassUsers = (classId, opts = {}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!classId) {
                setData(null);
                setLoading(false);
                setError(null);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const result = await Classes.getClassUsers(classId, opts);
                if (cancelled) return;
                const usersList = Array.isArray(result)
                    ? result
                    : result?.students ?? result?.classUsers ?? result?.users ?? [];
                setData(usersList);
            } catch (err) {
                if (!cancelled) {
                    setError(err);
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [classId, refreshTrigger, opts.include, opts.studentLogin, opts.grades]);

    const refetch = useCallback(() => {
        setRefreshTrigger((t) => t + 1);
    }, []);

    const users = Array.isArray(data) ? data : [];

    return { data, users, loading, error, refetch };
};

export default useClassUsers;
